import { useCallback } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { USER_PROFILE_QUERY_KEY } from 'constants/Constant';
import { DEFAULT_M_ORGANISATION_NAME } from 'constants/appConstants';
import { MOrganisation } from 'interfaces/IMasterDb';
import {
  PartialUserProfile,
  TeamAccess,
} from 'interfaces/IUserProfile';
import { MasterDbService } from 'services/MasterDbServices';
import { ProfileServices } from 'services/ProfileServices';
import { v4 as uuidv4 } from 'uuid';
import {
  buildMBase,
  buildMWriteBody,
  readDeviceId,
} from 'utils/mDocumentFactory';

// One resolution in flight per session, so two quick clicks can't each create
// a personal M organisation.
let inFlightResolution: Promise<string> | null = null;

// ─── Shared exports for 403-retry in mutation hooks ──────────────────────────

/** True when an Axios response carries a 403 status. */
export const isForbidden = (err: unknown): boolean => {
  if (err && typeof err === 'object' && 'response' in err) {
    return (
      (err as { response?: { status?: number } }).response?.status === 403
    );
  }
  return false;
};

/**
 * Clears cached morgAccesses so the next resolve() is forced to call GetUser.
 * Only wipes M-org data — the rest of the profile snapshot is preserved.
 */
export const clearMOrgAccessCache = (queryClient: QueryClient): void => {
  queryClient.setQueryData<PartialUserProfile>(
    [USER_PROFILE_QUERY_KEY],
    (old) =>
      old
        ? {
            ...old,
            morgAccesses: [],
            morganisations: [],
            teamAccesses: (old.teamAccesses ?? []).map((t) => ({
              ...t,
              morgAccesses: [],
            })),
          }
        : old
  );
};

// ─── Internal helpers ─────────────────────────────────────────────────────────

// Oldest active MOrganisation; null when every candidate fails validation.
const pickOldestActiveOrgId = async (
  orgIds: string[]
): Promise<string | null> => {
  const results = await Promise.allSettled(
    orgIds.map((id) => MasterDbService.GetMOrganisation(id))
  );
  const orgs = results
    .filter(
      (r): r is PromiseFulfilledResult<MOrganisation> =>
        r.status === 'fulfilled' && r.value?.isActive !== false
    )
    .map((r) => r.value);
  if (!orgs.length) return null;
  const time = (org: MOrganisation) =>
    Date.parse(org.dateCreated ?? '') || Number.MAX_SAFE_INTEGER;
  return [...orgs].sort((a, b) => time(a) - time(b))[0].id;
};

const pickValidOrgId = async (
  profile: PartialUserProfile
): Promise<string | null> => {
  const candidates = [
    ...(profile.morgAccesses ?? []).map((a) => a.orgId).filter(Boolean),
    ...(profile.teamAccesses ?? [])
      .flatMap((t) => t.morgAccesses ?? [])
      .map((a) => a.orgId)
      .filter(Boolean),
  ];
  if (!candidates.length) return null;
  return pickOldestActiveOrgId(candidates);
};

// Merge only morgAccesses from fresh into cached team entries.
// Deleted teams (in cache but not in fresh) are removed.
// New teams (in fresh but not in cache) are added with morgAccesses only —
// their access level is taken from fresh; orgAccesses/gorgAccesses are not
// part of TeamAccess and are not affected.
const mergeFreshTeamAccesses = (
  cached: TeamAccess[],
  fresh: TeamAccess[]
): TeamAccess[] => {
  const cachedMap = new Map(cached.map((t) => [t.teamId, t]));
  return fresh.map((freshTeam) => {
    const cachedTeam = cachedMap.get(freshTeam.teamId);
    return cachedTeam
      ? { ...cachedTeam, morgAccesses: freshTeam.morgAccesses ?? [] }
      : {
          teamId: freshTeam.teamId,
          access: freshTeam.access,
          morgAccesses: freshTeam.morgAccesses ?? [],
        };
  });
  // Teams in cached but absent from fresh are not included → deleted teams
  // are removed from the cache automatically.
};

// Surgical cache write: only morg-related fields are updated so the T₁
// profile snapshot from login is not overwritten. When the cache is empty
// (first call), the full fresh profile is stored as the initial value.
const writeProfileToCache = (
  queryClient: QueryClient,
  fresh: PartialUserProfile
): void => {
  queryClient.setQueryData<PartialUserProfile>(
    [USER_PROFILE_QUERY_KEY],
    (old) =>
      old
        ? {
            ...old,
            morgAccesses: fresh.morgAccesses,
            morganisations: fresh.morganisations,
            teamAccesses: mergeFreshTeamAccesses(
              old.teamAccesses ?? [],
              fresh.teamAccesses ?? []
            ),
          }
        : fresh
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns a function that resolves the M organisation id to write to.
 * Cache-first: reads cached morgAccesses before calling GetUser. Only
 * morgAccesses and morganisations are written to the shared cache, preserving
 * the T₁ profile snapshot from login. All candidates are validated via
 * GetMOrganisation + isActive. Web never merges organisations; mobile
 * reconciles them.
 */
export const useResolveMOrgId = () => {
  const queryClient = useQueryClient();

  const resolve = useCallback(async (): Promise<string> => {
    // 1. Cache-first: avoid GetUser when a valid mOrgId is already known
    const cached = queryClient.getQueryData<PartialUserProfile>([
      USER_PROFILE_QUERY_KEY,
    ]);
    if (cached) {
      const id = await pickValidOrgId(cached);
      if (id) return id;
    }

    // 2. No valid id in cache — fetch fresh profile (surgical cache update)
    const fresh = (await ProfileServices.GetUser()) as PartialUserProfile;
    writeProfileToCache(queryClient, fresh);
    const id = await pickValidOrgId(fresh);
    if (id) return id;

    // 3. No valid MOrg found — create "Default master data"
    const newId = uuidv4();
    const body = buildMWriteBody(
      buildMBase({
        id: newId,
        orgId: newId,
        deviceId: readDeviceId(),
      }),
      {
        orgName: DEFAULT_M_ORGANISATION_NAME,
        userAccesses: [],
        teamAccesses: [],
      }
    );
    await MasterDbService.CreateMOrganisation(body);

    // Refresh morgAccesses so the new org appears in the cache
    const afterCreate = (await ProfileServices.GetUser()) as PartialUserProfile;
    writeProfileToCache(queryClient, afterCreate);

    return newId;
  }, [queryClient]);

  return useCallback((): Promise<string> => {
    if (!inFlightResolution) {
      inFlightResolution = resolve().finally(() => {
        inFlightResolution = null;
      });
    }
    return inFlightResolution;
  }, [resolve]);
};

export default useResolveMOrgId;
